import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const RelatedArticles = () => {
  const relatedArticles = [
    {
      id: 'ai-automotive-excellence',
      title: 'Intelligence Artificielle dans l\'Excellence Automobile',
      category: 'IA & Innovation',
      excerpt: 'Découvrez comment l\'IA révolutionne les processus de qualité et d\'innovation dans l\'industrie automobile moderne.',
      readTime: '8 min',
      publishDate: '5 Nov 2025',
      author: 'Dr. Alexandre Dupont',
      link: '/ai-in-automotive-excellence-article',
      featured: true,
      tags: ['Intelligence Artificielle', 'Qualité', 'Innovation', 'Industrie 4.0'],
      imageAlt: 'Artificial intelligence concept with automotive manufacturing robots and digital interfaces representing AI excellence in automotive industry'
    },
    {
      id: 'digital-transformation-roadmap',
      title: 'Feuille de Route de la Transformation Digitale',
      category: 'Stratégie Digitale',
      excerpt: 'Guide complet pour planifier et exécuter une transformation digitale réussie dans l\'industrie automotive.',
      readTime: '12 min',
      publishDate: '28 Oct 2025',
      author: 'Marie-Claire Bernard',
      link: '/digital-transformation-roadmap-article',
      featured: false,
      tags: ['Transformation Digitale', 'Stratégie', 'Change Management', 'ROI'],
      imageAlt: 'Digital transformation roadmap diagram showing strategic planning phases for automotive industry digital evolution'
    },
    {
      id: 'future-skills-automotive',
      title: 'Compétences du Futur dans l\'Automobile',
      category: 'Prospective RH',
      excerpt: 'Analyse prospective des compétences critiques pour l\'industrie automobile des 10 prochaines années.',
      readTime: '10 min',
      publishDate: '15 Oct 2025',
      author: 'Sophie Renard',
      link: '/future-skills-automotive-article',
      featured: false,
      tags: ['Prospective', 'Compétences', 'RH', 'Formation'],
      imageAlt: 'Future automotive skills visualization with emerging technologies and professional development pathways'
    },
    {
      id: 'business-therapy-innovation',
      title: 'Business Thérapie : Guérir les Blocages Organisationnels',
      category: 'Business Thérapie',
      excerpt: 'Méthodologie innovante pour identifier et résoudre les dysfonctionnements organisationnels chroniques.',
      readTime: '15 min',
      publishDate: '8 Oct 2025',
      author: 'Jean-Philippe Martin',
      link: '/business-therapy-innovation-article',
      featured: false,
      tags: ['Business Thérapie', 'Organisation', 'Performance', 'Diagnostic'],
      imageAlt: 'Business therapy concept showing organizational healing and performance improvement methodologies'
    },
    {
      id: 'ai-powered-training',
      title: 'Formation Augmentée par l\'Intelligence Artificielle',
      category: 'EdTech & IA',
      excerpt: 'Comment l\'IA personnalise et optimise les parcours de formation pour maximiser l\'efficacité pédagogique.',
      readTime: '9 min',
      publishDate: '22 Sep 2025',
      author: 'Dr. Émilie Rousseau',
      link: '/ai-powered-training-article',
      featured: false,
      tags: ['IA', 'Formation', 'Personnalisation', 'EdTech'],
      imageAlt: 'AI-powered training platform interface showing personalized learning paths and adaptive education technology'
    },
    {
      id: 'leadership-digital-era',
      title: 'Leadership à l\'Ère du Numérique',
      category: 'Leadership',
      excerpt: 'Les qualités essentielles du leadership moderne pour diriger efficacement dans un environnement digital.',
      readTime: '11 min',
      publishDate: '14 Sep 2025',
      author: 'Pierre-Antoine Dubois',
      link: '/leadership-digital-era-article',  
      featured: false,
      tags: ['Leadership', 'Management', 'Digital', 'Soft Skills'],
      imageAlt: 'Digital leadership concept with modern management techniques and team collaboration in digital environment'
    }
  ];

  const featuredArticle = relatedArticles?.find(article => article?.featured);
  const otherArticles = relatedArticles?.filter(article => !article?.featured);

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Articles Complémentaires</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Approfondissez vos connaissances avec notre sélection d'articles experts sur l'intelligence artificielle et le développement des compétences.
          </p>
        </div>

        {/* Featured Article */}
        {featuredArticle && (
          <div className="mb-16">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                {/* Featured Content */}
                <div className="lg:col-span-8 p-8 lg:p-12">
                  <div className="flex items-center space-x-3 mb-6">
                    <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold">
                      Article Phare
                    </span>
                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                      {featuredArticle?.category}
                    </span>
                  </div>
                  
                  <h3 className="text-3xl font-bold text-gray-900 mb-4 leading-tight">
                    {featuredArticle?.title}
                  </h3>
                  
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    {featuredArticle?.excerpt}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {featuredArticle?.tags?.map((tag, index) => (
                      <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-6 text-sm text-gray-600">
                      <div className="flex items-center space-x-2">
                        <Icon name="User" size={16} />
                        <span>{featuredArticle?.author}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="Calendar" size={16} />
                        <span>{featuredArticle?.publishDate}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="Clock" size={16} />
                        <span>{featuredArticle?.readTime}</span>
                      </div>
                    </div>
                    <Link
                      to={featuredArticle?.link}
                      className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                    >
                      <span>Lire l'Article</span>
                      <Icon name="ArrowRight" size={16} />
                    </Link>
                  </div>
                </div>

                {/* Featured Visual */}
                <div className="lg:col-span-4 bg-gradient-to-br from-blue-500 to-purple-600 p-8 lg:p-12 flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="w-24 h-24 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Icon name="Zap" size={48} className="text-white" />
                    </div>
                    <h4 className="text-xl font-bold mb-3">Article Recommandé</h4>
                    <p className="text-blue-100 text-sm">
                      Notre analyse experte sur l'IA dans l'excellence automobile
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Other Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {otherArticles?.map((article) => (
            <article key={article?.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              {/* Article Header */}
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <span className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium">
                    {article?.category}
                  </span>
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Icon name="BookOpen" size={20} className="text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold leading-tight">
                  {article?.title}
                </h3>
              </div>

              {/* Article Content */}
              <div className="p-6 space-y-4">
                <p className="text-gray-700 leading-relaxed text-sm">
                  {article?.excerpt}
                </p>

                <div className="flex flex-wrap gap-2">
                  {article?.tags?.slice(0, 3)?.map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-xs text-gray-600">
                      <Icon name="User" size={12} />
                      <span>{article?.author}</span>
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={12} />
                        <span>{article?.publishDate}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Clock" size={12} />
                        <span>{article?.readTime}</span>
                      </div>
                    </div>
                  </div>
                  <Link
                    to={article?.link}
                    className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors duration-200"
                  >
                    <span>Lire</span>
                    <Icon name="ArrowRight" size={14} />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center bg-white rounded-2xl shadow-xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Restez Informé des Dernières Innovations</h3>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Abonnez-vous à notre newsletter pour recevoir nos analyses exclusives sur l'IA, 
            le développement des compétences et les tendances de l'industrie automobile.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Votre adresse e-mail"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 whitespace-nowrap">
              S'abonner
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Pas de spam. Désabonnement possible à tout moment.
          </p>
        </div>

        {/* Blog Navigation */}
        <div className="mt-12 text-center">
          <Link
            to="/blog-hub"
            className="inline-flex items-center space-x-2 bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Retour au Blog</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RelatedArticles;